import InventoryMap from "#/components/ui/InventoryMap";
import { useClient } from "#/hook/useClient";
import type { Item } from "#/types/items";
import { IconMapPinOff } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface LastKnownPositionSectionProps {
  item: Pick<Item, "latitude" | "longitude"> | undefined;
}

const PIN_ZOOM = 15;
const FALLBACK_PIN_COLOR = "#3b82f6";

/** `0` means "never geocoded" across the platform (see InventoryMap), so it is not a position. */
const toCoordinate = (value: number | string | null | undefined): number | null => {
  const parsed = typeof value === "string" ? parseFloat(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
};

/**
 * Where the item was on its most recent scan that carried GPS coordinates.
 * Scans overwrite `item.latitude` / `item.longitude`, so this is the last known
 * position, not a live one.
 */
const LastKnownPositionSection = ({ item }: LastKnownPositionSectionProps) => {
  const { t } = useTranslation();
  const { client } = useClient();

  const latitude = toCoordinate(item?.latitude);
  const longitude = toCoordinate(item?.longitude);
  const hasPosition = latitude !== null && longitude !== null;

  return (
    <div className="flex w-full flex-col gap-3">
      <span className="text-sm text-gray-600">{t("items.position.description")}</span>
      {hasPosition ? (
        <>
          <div className="flex h-[350px] w-full overflow-hidden rounded-lg border border-gray-200">
            <InventoryMap
              markers={[{ latitude, longitude, color: client?.color || FALLBACK_PIN_COLOR }]}
              zoom={PIN_ZOOM}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t("items.position.coordinates")}: {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </span>
        </>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-600">
          <IconMapPinOff size={20} className="shrink-0 text-gray-400" />
          <span>{t("items.position.none")}</span>
        </div>
      )}
    </div>
  );
};

export default LastKnownPositionSection;
